<script setup>
import forEach from 'lodash/forEach';
import findIndex from 'lodash/findIndex';
import {useDebug} from "../../composables/useDebug";
import {computed, inject, nextTick, onMounted, ref, watch} from "vue";
import {useSearchMixins} from "../../composables/useSearchMixins";
import FacetsList from './components/FacetList.vue';
import SearchForm from './components/SearchForm.vue';
import SearchResults from './components/SearchResults.vue';
import Spinner from '../LoadingSpinner/LoadingSpinner.vue';
import {useSearchStore} from "./store/useSearchStore";
import axios from "../../setup/axios";
import {useRouter} from "vue-router";

const API_ENDPOINT = '/o/merkur-2021-search/tgb/search';
const store = useSearchStore();

const {log, error} = useDebug();
const $bus = inject('$bus');
const $router = useRouter();

const props = defineProps({
    dataPortletId: {
        type: String,
        default: null,
    },
    dataSites: {
        type: String,
        default: [],
    },
    dataPlaceholder: {
        type: String,
        default: "",
    },
    dataHideDocumentTypes: {
        type: String,
        default: "true"
    }
});

const {defaultSearch} = useSearchMixins();
const abortController = ref(null);
const changedKeyword = ref(true);
const isDocumentQuery = ref(false);
const suggestions = ref(null);
const searchGroupIds = ref([]);
const results = ref([]);
const pageResults = ref([]);
const allResults = ref([]);
const totalResults = ref(0);
const currentPage = ref(null);
const changeFacets = ref(true);

const facets = ref([]);
const documentTypeFacets = ref([]);
const selectedFacets = ref([]);
const busy = ref(true);
const showFacets = ref(true);
const errors = ref([]);
const response = ref({});
const showDocumentTypes = ref(false);
const selectedDocumentTypes = ref([]);
const doneSearch = ref(false);

const theEndRef = ref(null);
const searchQuery = computed(() => $router.currentRoute.value.params.query);
const dataPortletId = computed(() => props.dataPortletId);
const dataPlaceholder = computed(() => props.dataPlaceholder);
const dataHideDocumentTypes = computed(() => props.dataHideDocumentTypes);
const dataSites = computed(() => props.dataSites);

const range = computed(() => {
    if (currentPage === null) {
        return {
            from: 1,
            to: defaultSearch.options.resultsPerPage,
        };
    }

    const calculatedEnd = currentPage * defaultSearch.options.resultsPerPage;
    const calculatedStart = calculatedEnd - defaultSearch.options.resultsPerPage;

    return {
        from: calculatedStart < 1 ? 1 : calculatedStart,
        to: calculatedEnd > totalResults ? totalResults : calculatedEnd,
    };
});

const totalNumberOfPages = computed(() => {

    if (currentPage.value === null || totalResults.value <= 0) {
        return 1;
    }

    return Math.ceil(totalResults.value / defaultSearch.options.resultsPerPage);
});

watch(searchQuery, (query) => {
    nextTick(() => {

        selectedFacets.value = [];
        store.searchQuery = query;
        handleSearch(query);
    });
});

const hideDocumentTypes = computed(() => {
    if (!dataHideDocumentTypes.value || (dataHideDocumentTypes.value !== 'true' && dataHideDocumentTypes.value !== 'false')) {
        return 'true';
    } else {
        return dataHideDocumentTypes.value;
    }
});

const cssDocumentTypes = computed(() => {
    return hideDocumentTypes.value === 'false' ? 'show-document-types' : 'hide-document-types'
});


const initializeEvents = () => {

    const observer = new IntersectionObserver(entries => {
        if (busy.value) {
            return;
        }
        entries.forEach(entry => {
            if (entry.target.classList.contains('the-end') && entry.isIntersecting === true) {
                handleInfiniteScrolling();
            }
        });
    }, {
        root: document.getElementById('wrapper')
    });

    if (theEndRef !== undefined && theEndRef.value !== undefined) {
        observer.observe(theEndRef.value);
    }

    $bus.on('updateKeyword', (changed) => {
        changedKeyword.value = true;
    });

    $bus.on('updateSelectedFacets', updateSelectedFacets);
    $bus.on('switchResultPage', handleResultPageChange);
    $bus.on('extractFacets', (v) => setExtractFacets(v));

};

const handleInfiniteScrolling = () => {
    if (busy.value) {
        return;
    }

    if (currentPage.value === null) {
        return;
    }
    if (totalNumberOfPages.value <= currentPage.value) {
        return;
    }

    handleSearch(null, currentPage.value + 1);
}

const handleResultPageChange = (page) => {
    handleSearch(null, page);
};

const resetRequest = () => {
    abortController.value = null;
    busy.value = false;

};

const updateSelectedFacets = (facet) => {

    if (facet && facet.id !== 'ALL') {
        selectedFacets.value = [facet];
    } else {
        selectedFacets.value = [];
    }

    let newResults = [];
    for (let x = 0; x < results.value.length; x++) {
        if (facet.id === results.value[x].type || facet.id === 'ALL') {
            results.value[x].show = true;
        } else {
            results.value[x].show = false;
        }

        newResults.push(results.value[x]);
    }

    if (facet.id === 'com.liferay.document.library.kernel.model.DLFileEntry') {
        isDocumentQuery.value = true;
        showDocumentTypes.value = true;
    } else {
        isDocumentQuery.value = false;
        showDocumentTypes.value = false;
        selectedDocumentTypes.value = [];
    }

    results.value = newResults;

    handleSearch();
};

const escape = (string) => {
    return string.replace(/"/g, '\\"').replace(/'/g, "\\'");
};

const resetResults = () => {
    results.value = [];
    doneSearch.value = false;

    totalResults.value = 0;
    store.searchQuery = null;
    facets.value = [];
    selectedFacets.value = [];
    window.history.replaceState(null, null, window.location.pathname);
};

const filterDocumentType = (element) => {

    element.selected = element.selected !== true;
    handleSearch();
};

const getSelectedFacets = () => {

    const facets = [];

    forEach(selectedFacets.value, facet => {
        if (facet.type === 'vocabularies' && facet.facets.length === 0) {
            return;
        }

        if (facet.type === 'assettypes' && facet.facets.length === 0) {
            return;
        }

        if (facet.id === null) {
            return;
        }

        facets.push({
            id: facet.id,
            type: facet.type,
        });
    });

    if (isDocumentQuery.value) {
        forEach(documentTypeFacets.value, docFacet => {
            if (docFacet.selected) {
                facets.push({
                    id: docFacet.id,
                    type: docFacet.type
                });
            }
        });
    }

    return facets;
};

const handleResult = (result, extractFacets) => {
    log?.('🕵️‍ Processing data:', result);

    // Load the results in the local data variable
    if (result) {
        selectedDocumentTypes.value = [];
        response.value = result;
        let curResults = result.results;
        if (curResults) {
            for (let x = 0; x < curResults.length; x++) {
                if (!selectedFacets.value || !selectedFacets.value[0]) {
                    curResults[x].show = true;
                } else {
                    curResults[x].show = selectedFacets.value[0].id === curResults[x].type;
                }
            }
        }

        if (result.request.currentPage === 1) {
            results.value = curResults;
        } else {
            results.value = results.value.concat(curResults);
        }

        totalResults.value = result.hasOwnProperty('totalCount') ? result.totalCount : results.value.length;

        if (result.hasOwnProperty('pageResults')) {
            pageResults.value = result.pageResults;
        }

        const startingIndex = result.hasOwnProperty('startIndex') ? result.startIndex : 0;
        currentPage.value = Math.ceil(startingIndex / defaultSearch.options.resultsPerPage) + 1;

        if (changeFacets.value) {
            const index = findIndex(result.facets, o => o.name === 'search_content_types');

            if (index !== -1) {
                facets.value = result.facets[index].facets;
            }
        }

        if (changedKeyword.value && isDocumentQuery.value && result.documentFacets) {
            documentTypeFacets.value = result.documentFacets;
            changedKeyword.value = false;
        }

        document.body.classList.remove('has-scrolllock');
        if (result.facets && result.facets.length > 0) {
            changeFacets.value = true;
        }
    }
    doneSearch.value = true;

};

const focusFirstChild = () => {
    if (inputElement.value !== null) {
        inputElement.value.focus();
    }
}

const resetSearch = () => {
    doneSearch.value = false;
    results.value = [];
    facets.value = [];
    totalResults.value = 0;
    selectedFacets.value = [];
}

const restartSearch = () => {
    store.searchQuery = null;
    doneSearch.value = false;
    results.value = [];

    facets.value = [];
    selectedFacets.value = [];
    totalResults.value = 0;
    $router.push(
        {
            params: {
                query: '',
            },
        }
    )
        .catch(e => {
            error?.('🕵️‍ Error router response:', e);
        });

    nextTick(() => {
        handleSearch();
    });
};

const startSearch = () => {
    handleSearch(store.searchQuery);
};

$bus.on('restartSearch', restartSearch);
$bus.on('resetSearch', resetSearch);
$bus.on('startSearch', startSearch);

const inputElement = ref(null);

const inputRef = (element) => {
    inputElement.value = element.value;
}

$bus.on('SearchInputRefEmitter', inputRef);

const handleSearch = (query = null, page = 1) => {
    busy.value = true;

    changeFacets.value = query !== null;
    if (changeFacets.value) {
        facets.value = null;
        store.selectedFacet = null;
    }
    store.searchQuery = query || store.searchQuery;

    page = page > 1 ? page : 1;

    if (abortController.value) {
        abortController.value.abort();
    }

    abortController.value = new AbortController();
    // Update the address bar of the browser with the search query
    if ((store.searchQuery === undefined || store.searchQuery === null || store.searchQuery.length === 0) && suggestions.value !== null) {
        if (suggestions.value.results !== undefined && suggestions.value.results.length > 0) {
            handleResult(suggestions.value);
            resetRequest();

            if (page === 1) {
                focusFirstChild();
            }
        }

    } else if (store.searchQuery !== null && store.searchQuery.length >= defaultSearch.options.minimumSearchQueryLength) {
        const query = store.searchQuery.trim();

        log?.('🔎️ searching for: ', query, page);

        $router.push(
            {
                params: {
                    query: query,
                },
            }
        )
            .catch(e => {
                error?.('🕵️‍ Error router response:', e);
            });

        const data = {
            plid: window.Liferay.ThemeDisplay.getPlid(),
            portletId: dataPortletId.value,
            keywords: escape(query),
            currentPage: page,
            pageSize: defaultSearch.options.resultsPerPage,
            facets: getSelectedFacets(),
            locale: window.Liferay.ThemeDisplay.getLanguageId(),
            searchGroupIds: searchGroupIds.value,
            scopeGroupId: window.Liferay.ThemeDisplay.getScopeGroupIdOrLiveGroupId(),
        };

        axios.post(API_ENDPOINT, data, {responseType: 'json', signal: abortController.value.signal})
            .then(response => {
                log?.('🕵️‍ Search response:', response);

                handleResult(response.data);
                resetRequest();

                changeFacets.value = false;

            })
            .catch(e => {
                error?.('🕵️‍ Error response:', e);

                errors.value.push(error);
                resetRequest();
            })
            .finally(() => {
                resetRequest();
                if (page === 1) {
                    focusFirstChild();
                }
            });
    } else {
        resetResults();
        resetRequest();
        if (page === 1) {
            focusFirstChild();
        }
    }
};

onMounted(() => {

    searchGroupIds.value.push(window.Liferay.ThemeDisplay.getScopeGroupIdOrLiveGroupId())

    if (dataSites.value) {
        dataSites.value = dataSites.value.trim();
        let aSites = dataSites.value.split(",");
        for (let x = 0; x < aSites.length; x++) {
            let curGroupId = aSites[x].trim();
            if (curGroupId !== '') {
                searchGroupIds.value.push(aSites[x].trim());
            }
        }
    }

    if (document.body.classList.contains('active-quick-search')) {
        document.body.classList.remove('active-quick-search');
    }

    if (window.searchSuggestions &&
        window.searchSuggestions.results &&
        window.searchSuggestions.results.length > 0) {

        suggestions.value = window.searchSuggestions;
        suggestions.value.request = {
            "currentPage": 1,
        };
    } else {
        suggestions.value = null;
    }

    initializeEvents();
    handleSearch();

    if (!store.searchQuery) {
        busy.value = false;
    }

});

</script>

<template>
    <div class="search" :class="{ 'is-searching': busy }">

        <div class="form-container container-fluid">
            <div class="search-container row">
                <div class="col-sm-3"></div>
                <SearchForm ref="form" :busy="busy"
                            :placeholder="dataPlaceholder"
                            @reset="resetResults"
                            type="search"
                            @search="handleSearch" class="col-sm-6"
                />
                <div class="col-sm-3"></div>
            </div>
            <div class="search-container row">
                <div class="col-sm-3"></div>
                <div class="results col-sm-6">
                    <FacetsList v-if="showFacets && results && results.length" :facets="facets"
                                :selected-facets="selectedFacets"
                                :busy="busy"
                    />
                </div>
                <div class="col-sm-3"></div>
            </div>
        </div>
        <div class="container-fluid search-container">
            <div v-if="results && results.length" class="results row">
                <div v-if="showDocumentTypes" class="col-sm-1"></div>
                <div v-if="showDocumentTypes" class="col-sm-2" :class="cssDocumentTypes" id="search-document-types">
                    <ul>
                        <li v-for="documentTypFacet in documentTypeFacets">
                            <div class="checkbox">
                                <label :for="documentTypFacet.id">
                                    <input :id="documentTypFacet.id" type="checkbox"
                                           :name="documentTypFacet.name"
                                           :checked="documentTypFacet.selected"
                                           @click="filterDocumentType(documentTypFacet)">
                                    {{ documentTypFacet.name }}
                                </label>
                            </div>
                        </li>
                    </ul>
                </div>
                <div v-else class="col-sm-3"></div>
                <SearchResults
                    v-if="results && results.length"
                    facets-wrapper
                    :results="results"
                    :search-query="store.searchQuery"
                    :current-page="currentPage"
                    :total-results="totalResults"
                    :selected-facets="selectedFacets"
                    :document-types="documentTypeFacets"
                    :busy="busy"
                    class="col-sm-6"
                />
                <div class="col-sm-3"></div>
            </div>

            <div class="empty search-empty"
                 v-if="doneSearch && !busy && (!results || !results.length) && store.searchQuery !== null && store.searchQuery !== undefined && store.searchQuery.length >= defaultSearch.options.minimumSearchQueryLength">
                <h1 class="empty-heading text-title title is-1" v-html="$t('search.oops')"></h1>
                <p class="empty-text has-regular-text" v-html="$t('search.noResults')"></p>
            </div>
        </div>
        <div class="the-end"
             ref="theEndRef">
            <Spinner v-if="busy" class="search-spinner" :size="'large'" :line-fg-color="'#64B32C'"/>
        </div>
    </div>
</template>

